import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {getJobVacancies, getStaffMembers} from "../../api/client";

const staffAdvantages = [
    "Опыт работы с грузовыми марками FORD, Mercedes-Benz, MAN, DAF, VOLVO и SCANIA.",
    "Понятная коммуникация по срокам, стоимости и составу работ.",
    "Ответственный подход к диагностике, ремонту и проверке результата.",
];
const careerAdvantages = [
    "Стабильная занятость и понятный рабочий процесс.",
    "Работа с грузовой техникой и реальными техническими задачами.",
    "Возможность профессионального роста внутри команды.",
    "Открытая коммуникация с руководством без лишней бюрократии.",
    "Условия можно обсудить напрямую перед выходом на работу.",
];
const fallbackVacancies = [
    {id: "automotive-mechanic", title: "Автомеханик", description: "Ремонт и обслуживание грузовой техники."},
    {id: "auto-locksmith", title: "Автослесарь", description: "Слесарные работы, диагностика неисправностей и подготовка техники."},
    {id: "diagnostician", title: "Диагност", description: "Компьютерная диагностика грузовых автомобилей."},
    {id: "auto-electrician", title: "Автоэлектрик", description: "Электрика, датчики, проводка и электронные системы."},
    {id: "truck-repair-specialist", title: "Специалист по ремонту грузовой техники", description: "Комплексный ремонт грузовых автомобилей и агрегатов."},
    {id: "assistant-master", title: "Помощник мастера", description: "Помощь мастерам на участке и выполнение базовых задач."},
    {id: "request-manager", title: "Администратор / менеджер по заявкам", description: "Обработка обращений клиентов и координация записи."},
];

function isBrokenVacancyText(value) {
    if (!value) {
        return true;
    }

    const questionMarksCount = (value.match(/\?/g) || []).length;
    return questionMarksCount >= Math.max(3, Math.floor(value.length * 0.35));
}

function Staff() {
    const [staffMembers, setStaffMembers] = useState([]);
    const [vacancies, setVacancies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let isMounted = true;

        Promise.allSettled([getStaffMembers(), getJobVacancies()])
            .then(([staffResult, vacanciesResult]) => {
                if (isMounted) {
                    if (staffResult.status === "fulfilled") {
                        setStaffMembers(staffResult.value);
                        setError("");
                    } else {
                        setError(staffResult.reason.message);
                    }

                    if (vacanciesResult.status === "fulfilled") {
                        setVacancies(vacanciesResult.value);
                    } else {
                        setVacancies([]);
                    }
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const hasReadableVacancies = vacancies.some((vacancy) => (
        !isBrokenVacancyText(vacancy.title) && !isBrokenVacancyText(vacancy.description)
    ));
    const visibleVacancies = hasReadableVacancies ? vacancies : fallbackVacancies;

    return (
        <main className="staff-page">
            <section className="staff-hero">
                <div className="staff-hero-content">
                    <span className="staff-eyebrow">Команда сервиса</span>
                    <h1>Персонал Трак-Центр 24</h1>
                    <p className="staff-hero-description">
                        В нашей команде работают профессионалы своего дела: специалисты, которые знают грузовую технику,
                        внимательно относятся к деталям и отвечают за качество результата. Мы ценим точную диагностику,
                        понятную коммуникацию и работу, после которой клиент спокойно возвращает автомобиль в рейс.
                    </p>
                </div>

                <div className="staff-hero-actions">
                    <Link className="staff-careers-link" to="/staff/apply">
                        Набор персонала
                    </Link>
                    <Link className="staff-home-link" to="/">
                        На главную
                    </Link>
                </div>
            </section>

            <section className="staff-section">
                <div className="staff-section-heading">
                    <span>Специалисты</span>
                    <h2>Кто работает с техникой</h2>
                </div>

                {isLoading && <p className="staff-status">Загружаем персонал...</p>}
                {error && <p className="staff-status staff-status-error">{error}</p>}

                {!isLoading && !error && staffMembers.length === 0 && (
                    <p className="staff-status">Список персонала пока пуст. Администратор может добавить сотрудников в админке.</p>
                )}

                {!isLoading && !error && staffMembers.length > 0 && (
                    <div className="staff-grid">
                        {staffMembers.map((member) => (
                            <article className="staff-card" key={member.id}>
                                <div className="staff-avatar" aria-hidden="true">
                                    {member.name.slice(0, 1)}
                                </div>
                                <h3>{member.name}</h3>
                                <strong>{member.role}</strong>
                                <p>{member.description}</p>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <section className="staff-section staff-info-panel">
                <div>
                    <span className="staff-eyebrow">Подход</span>
                    <h2>Что важно в работе персонала</h2>
                </div>

                <ul>
                    {staffAdvantages.map((advantage) => (
                        <li key={advantage}>{advantage}</li>
                    ))}
                </ul>
            </section>

            <section className="staff-section staff-careers" id="careers">
                <div className="staff-careers-heading">
                    <span className="staff-eyebrow">Набор персонала</span>
                    <h2>Ищем специалистов в команду</h2>
                    <p>
                        Этот блок расположен отдельно от клиентских заявок: он не мешает посетителям сервиса, но дает
                        кандидатам понять, что можно обсудить работу напрямую.
                    </p>
                </div>

                <div className="staff-vacancy-grid">
                    {visibleVacancies.map((vacancy) => (
                        <article className="staff-vacancy-card" key={vacancy.id}>
                            <h3>{vacancy.title}</h3>
                            <p>{vacancy.description}</p>
                            <Link
                                className="staff-vacancy-link"
                                to={`/staff/apply?position=${encodeURIComponent(vacancy.title)}`}
                            >
                                Откликнуться
                            </Link>
                        </article>
                    ))}
                </div>
            </section>

            <section className="staff-section staff-career-advantages">
                <div className="staff-careers-heading">
                    <span className="staff-eyebrow">Преимущества работы</span>
                    <h2>Почему стоит выбрать нас</h2>
                    <p>Здесь пока базовые тезисы-заглушки. Позже их можно заменить на точные условия компании.</p>
                </div>

                <div className="staff-advantage-grid">
                    {careerAdvantages.map((advantage) => (
                        <article className="staff-advantage-card" key={advantage}>
                            {advantage}
                        </article>
                    ))}
                </div>
            </section>
        </main>
    );
}

export default Staff;
